/**
 * The user's newsletter subscription status, derived from the three snake_case booleans the API
 * projects onto every user payload from `user.marketingConsent.email.status`. They are mutually
 * exclusive, and all three are false when we have never asked, so the derivation is ordered
 * rather than a lookup.
 *
 * A formatter that takes an auth slice and gets the email status back. It reads no store and
 * holds no state. It behaves identically wherever it is called. React code goes through
 * `useEmailSubscribeStatus`, which subscribes to auth and runs it through here
 */

export const SUBSCRIBED = 'subscribed';
export const PENDING = 'pending';
export const UNSUBSCRIBED = 'unsubscribed';
export const NONE = 'none';

/**
 * @typedef {Object} EmailSubscribeStatus
 * @property {('subscribed'|'pending'|'unsubscribed'|'none')} status
 * @property {Boolean} isSubscribed       Confirmed subscriber
 * @property {Boolean} isPending          Sent a double opt-in email, not confirmed yet
 * @property {Boolean} isUnsubscribed     Asked us to stop
 * @property {Boolean} hasNeverSubscribed Never asked - not the same as unsubscribed
 * @property {Boolean} hasLocalConsent    Ticked a consent box this session, not yet at the CRM
 * @property {Boolean} hasOptedIn         Asked to subscribe, confirmed or not - do not re-prompt
 * @property {Boolean} canSubscribe       Show a subscribe form? Not if one is already outstanding
 * @property {Boolean} isAuthenticated
 * @property {String}  email              For sniper links and re-posts
 */

/**
 * @param  {Object} auth The `auth` redux slice - not the whole state
 * @return {EmailSubscribeStatus}
 */
const getEmailSubscribeStatus = (auth) => {
  const {
    authenticated = false,
    email = '',
    email_newsletter: subscribed = false,
    email_newsletter_pending: pending = false,
    email_newsletter_unsubscribed: unsubscribed = false,
    localNewsletterConsent = 0,
  } = auth || {};

  // Subscribed wins over pending, mirroring the backend invariant that a confirmed opt-in is
  // never downgraded back to pending
  let status = NONE;
  if (subscribed) {
    status = SUBSCRIBED;
  } else if (pending) {
    status = PENDING;
  } else if (unsubscribed) {
    status = UNSUBSCRIBED;
  }

  const isSubscribed = status === SUBSCRIBED;
  const isPending = status === PENDING;

  // We already have this address - confirmed, or pending in their inbox awaiting confirmation.
  const hasOptedIn = isSubscribed || isPending;

  return {
    status,
    isSubscribed,
    isPending,
    isUnsubscribed: status === UNSUBSCRIBED,
    hasNeverSubscribed: status === NONE,
    hasLocalConsent: !!localNewsletterConsent,
    hasOptedIn,
    canSubscribe: !hasOptedIn,
    isAuthenticated: !!authenticated,
    email,
  };
};

export default getEmailSubscribeStatus;
